import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@tyndo/ui-kit';
import { MoreHorizontal, type LucideIcon } from 'lucide-react';

/**
 * A single destination on the small-breakpoint bottom bar. Resolved by the
 * shell from the operator's entitlement-bounded module set (see AppShell's
 * `bottomNavCandidates`) — this component only renders what it is handed.
 */
export type BottomNavItem = { id: string; label: string; href: string; icon: LucideIcon };

interface MobileBottomNavProps {
  /** Destinations shown inline on the bar. */
  pinned: BottomNavItem[];
  /** Destinations that didn't fit; reachable through the "More" drawer. */
  overflow: BottomNavItem[];
  /** Whether the overflow drawer is open. */
  moreOpen: boolean;
  onMoreOpenChange: (open: boolean) => void;
}

/**
 * Mobile bottom-nav — only at < md. Keeps the top operator destinations one tap
 * away; anything beyond the pinned slots spills into a "More" drawer so the bar
 * never overflows.
 *
 * Extracted from AppShell so the shell frame stays under the per-view LOC
 * ceiling and this bar can be reasoned about (and restyled) on its own.
 */
export default function MobileBottomNav({
  pinned,
  overflow,
  moreOpen,
  onMoreOpenChange,
}: MobileBottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (href: string) =>
    location.pathname === href || (href !== '/' && location.pathname.startsWith(href));

  return (
    <>
      <nav
        className="glass-strong md:hidden fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-30 flex items-stretch h-16 rounded-full border border-border/60 shadow-[var(--shadow-overlay)] px-1.5"
        aria-label="Quick navigation"
      >
        {pinned.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Button
              key={item.id}
              type="button"
              variant="ghost"
              onClick={() => navigate(item.href)}
              className={`h-auto p-0 whitespace-normal font-normal [&_svg]:size-5 hover:bg-transparent my-2 mx-0.5 flex-1 flex flex-col items-center justify-center gap-0.5 rounded-full text-[10px] transition-colors [transition-timing-function:var(--ease-spring)] motion-reduce:transition-none ${
                active ? 'bg-primary/12 text-primary font-medium' : 'text-muted-foreground'
              }`}
              aria-current={active ? 'page' : undefined}
              aria-label={item.label}
            >
              <Icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Button>
          );
        })}
        {overflow.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => onMoreOpenChange(true)}
            className="h-auto p-0 whitespace-normal font-normal [&_svg]:size-5 hover:bg-transparent my-2 mx-0.5 flex-1 flex flex-col items-center justify-center gap-0.5 rounded-full text-[10px] text-muted-foreground transition-colors motion-reduce:transition-none"
            aria-label="More destinations"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
          >
            <MoreHorizontal className="w-5 h-5" />
            <span>More</span>
          </Button>
        )}
      </nav>

      {/* Bottom-nav overflow drawer — destinations that didn't fit on the
          pinned bar. Only mounted at < md and when there is overflow. */}
      {moreOpen && overflow.length > 0 && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-foreground/40 backdrop-blur-sm motion-reduce:transition-none"
            onClick={() => onMoreOpenChange(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="More destinations"
            className="glass-strong absolute bottom-[calc(5.5rem+env(safe-area-inset-bottom))] left-3 right-3 rounded-2xl border border-border/60 shadow-[var(--shadow-overlay)] max-h-[60vh] overflow-auto [transition-timing-function:var(--ease-spring)]"
          >
            <div className="grid grid-cols-3 gap-2 p-4">
              {overflow.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Button
                    key={item.id}
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      navigate(item.href);
                      onMoreOpenChange(false);
                    }}
                    className={`h-auto whitespace-normal font-normal [&_svg]:size-5 hover:bg-transparent flex flex-col items-center justify-center gap-1 rounded-xl border border-border/60 p-3 text-xs transition-colors motion-reduce:transition-none ${
                      active
                        ? 'bg-primary/12 text-primary border-primary/40'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-center leading-tight">{item.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
